// Content sourced from chesapeakecounselingcenter.com, rewritten warmly for polish while preserving factual information.
import { Facebook, Instagram, Youtube } from 'lucide-react';

export const SITE = {
  name: 'Chesapeake Counseling Center',
  short: 'CCC',
  tagline: 'Let Your Light Shine',
  domain: 'https://chesapeakecounselingcenter.com',
  description: 'You are not alone. Healing begins here. Compassionate, trauma-informed counseling in Chesapeake, VA — a digital sanctuary where you can rediscover the light that has always been yours.',
};

export const PHILOSOPHY = {
  eyebrow: 'Our Philosophy',
  headline: 'Let Your Light Shine',
  mission:
    'We believe that every person carries a light within them. Sometimes life’s challenges make that light difficult to see, but it is never lost. Through grace, compassion, courage, and hope, healing becomes possible. Our mission is to walk beside you as you rediscover the light that has always been yours.',
  lotus:
    'The lotus doesn’t avoid the mud — it grows through it. Healing isn’t about erasing the past; it’s about growing through it with compassion and resilience.',
};

export const VALUES = [
  {
    key: 'grace',
    title: 'Grace',
    icon: 'HeartHandshake',
    text: 'Meeting ourselves and others with compassion — especially when the road is hard.',
  },
  {
    key: 'gratitude',
    title: 'Gratitude',
    icon: 'Sun',
    text: 'Recognizing the light that shines even in difficult seasons.',
  },
  {
    key: 'love',
    title: 'Love',
    icon: 'Heart',
    text: 'Choosing connection over fear — with ourselves, our people, and our world.',
  },
  {
    key: 'courage',
    title: 'Courage',
    icon: 'Flame',
    text: 'Taking the next step forward, even when it feels small or uncertain.',
  },
  {
    key: 'faith',
    title: 'Faith',
    icon: 'Cross',
    text: 'Trusting in the power of something greater than the moment we’re in.',
  },
  {
    key: 'sound-mind',
    title: 'A Sound Mind',
    icon: 'Sparkles',
    text: 'Finding steadiness, clarity, and peace in the midst of life’s noise.',
  },
];

// Rebranded, transformation-focused labels for the top-level services grid.
// Each item maps to an existing detailed service page slug.
export const PATHS = [
  {
    slug: 'anxiety-therapy',
    title: 'Healing from Anxiety',
    short: 'Find steadiness beneath the noise. Return to calm, clarity, and confidence in your own mind.',
    icon: 'Wind',
  },
  {
    slug: 'trauma-therapy',
    title: 'Healing from Trauma',
    short: 'Reconnect with a sense of safety. Grow through what happened — not around it — with EMDR and somatic care.',
    icon: 'ShieldHeart',
  },
  {
    slug: 'couples-counseling',
    title: 'Healing Relationships',
    short: 'Rebuild connection, trust, and understanding. Learn to move toward each other, not away.',
    icon: 'Users',
  },
  {
    slug: 'individual-therapy',
    title: 'Finding Hope After Loss',
    short: 'Grief has no timeline. Find gentle, steady companionship as you carry what you love forward.',
    icon: 'Heart',
  },
  {
    slug: 'stress-burnout',
    title: 'Growing Through Life Transitions',
    short: 'Every season of change is an invitation. Meet it with grounded support, honest reflection, and hope.',
    icon: 'Sun',
  },
  {
    slug: 'womens-counseling',
    title: 'Reconnecting with Yourself',
    short: 'A warm, women-centered space to slow down, set boundaries, and rediscover the light within.',
    icon: 'Flower2',
  },
];

export const CONTACT = {
  name: 'Chesapeake Counseling Center',
  phone: '757-302-8362',
  phoneHref: 'tel:7573028362',
  email: 'admin@chesapeakecounselingcenter.com',
  address: '501 Cedar Rd., Suite 2A',
  city: 'Chesapeake, VA 23322',
  hours: [
    { day: 'Monday – Friday', time: '9:00 am – 5:00 pm' },
    { day: 'Saturday', time: 'By Appointment Only' },
    { day: 'Sunday', time: 'Closed' },
  ],
};

export const SOCIAL = [
  { label: 'Facebook', href: 'https://www.facebook.com/chescounseling1?checkpoint_src=1501092823525282', Icon: Facebook },
  { label: 'Instagram', href: 'https://www.instagram.com/chesapeakecc2018/', Icon: Instagram },
  { label: 'YouTube', href: 'https://www.youtube.com/channel/UCV6pGNcw8bKAqJyChujVV6g', Icon: Youtube },
];

export const NAV = [
  { label: 'Home', to: '/' },
  { label: 'Our Philosophy', to: '/philosophy' },
  { label: 'About', to: '/about' },
  { label: 'Meet the Team', to: '/team' },
  { label: 'Services', to: '/services' },
  { label: 'Resources', to: '/blog' },
  { label: 'Insurance & FAQs', to: '/insurance' },
  { label: 'Contact', to: '/contact' },
];

export const IMAGES = {
  heroLake: 'https://images.unsplash.com/photo-1487139975590-b4f1dce9b035?auto=format&fit=crop&w=1800&q=80',
  lotusLake: 'https://images.unsplash.com/photo-1560312473-d6e339d91639?auto=format&fit=crop&w=1600&q=80',
  sunriseWater: 'https://images.unsplash.com/photo-1594175268654-e60bea6c037c?auto=format&fit=crop&w=1800&q=80',
  officeWarm: 'https://images.unsplash.com/photo-1615800001964-5afd0ae8e49a?auto=format&fit=crop&w=1400&q=80',
  officeCozy: 'https://images.pexels.com/photos/5490367/pexels-photo-5490367.jpeg?auto=compress&cs=tinysrgb&w=1400',
  botanical1: 'https://images.unsplash.com/photo-1591808172074-f9d1b901535d?auto=format&fit=crop&w=1200&q=80',
  botanical2: 'https://images.unsplash.com/photo-1551077377-cda42c0990ae?auto=format&fit=crop&w=1200&q=80',
  // Brand imagery uploaded by client
  heroLotusSunrise: 'https://customer-assets.emergentagent.com/job_image-name-mapper/artifacts/z4yw7psg_a39a3945-ece8-4d58-8741-40d46a08bdcd.png',
  belongHere: 'https://customer-assets.emergentagent.com/job_image-name-mapper/artifacts/y59gapjd_9b2b00c7-7463-43a6-8ec2-0da4791a4c6c.png',
  thrive: 'https://customer-assets.emergentagent.com/job_image-name-mapper/artifacts/f3lnpy19_c04b727a-71a6-4d34-b137-a222c2bb43e0.webp',
  rootedInNature: 'https://customer-assets.emergentagent.com/job_image-name-mapper/artifacts/drn6pcq8_bb348595-ca25-40be-8a2f-8611b90abad3.png',
  missionBanner: 'https://customer-assets.emergentagent.com/job_image-name-mapper/artifacts/qq9t38vh_1509cec3-d23a-43c8-805e-b845df5d1362.png',
};

export const SERVICES = [
  {
    slug: 'trauma-therapy',
    title: 'Trauma Therapy',
    seoTitle: 'Trauma Therapy in Chesapeake, VA | Chesapeake Counseling Center',
    seoDesc: 'Compassionate trauma therapy in Chesapeake, VA. Evidence-based EMDR, somatic, and nervous system approaches to help you heal and move forward.',
    icon: 'ShieldHeart',
    short: 'Safely process past experiences, reduce emotional reactivity, and reconnect with a sense of safety and control.',
    intro: 'Trauma can leave you feeling stuck, overwhelmed, or disconnected from yourself. Even when life looks fine on the outside, you may feel easily triggered, constantly on edge, numb, or caught in patterns you can’t seem to break. Trauma isn’t only about what happened to you — it’s about how your body and mind learned to respond in order to survive.',
    experiencing: [
      'Easily triggered or emotionally reactive',
      'Constantly on edge or anxious',
      'Numb, disconnected, or shut down',
      'Stuck in patterns you can’t seem to break',
    ],
    modalities: [
      { title: 'Somatic Interventions', text: 'Body-based approaches to release stored trauma.' },
      { title: 'EMDR Therapy', text: 'Evidence-based processing for trauma and distressing memories.' },
      { title: 'Cognitive Processing', text: 'Cognitive and emotional processing techniques.' },
      { title: 'Nervous System Regulation', text: 'Tools to help regulate your body’s stress response.' },
    ],
    outcomes: [
      'Feel safer in your body',
      'Reduce emotional reactivity',
      'Let go of past experiences that still feel present',
      'Reconnect with yourself and your life',
    ],
    approach: 'We combine EMDR, somatic work, cognitive processing, and nervous system regulation — gently, at the pace that feels right for you. You don’t have to relive everything to heal, but you do deserve the right support.',
    related: ['emdr-therapy', 'anxiety-therapy', 'narcissistic-abuse-recovery'],
  },
  {
    slug: 'emdr-therapy',
    title: 'EMDR Therapy',
    seoTitle: 'EMDR Therapy in Chesapeake, VA | Chesapeake Counseling Center',
    seoDesc: 'EMDR therapy in Chesapeake, VA helps your brain process and release distressing experiences. Evidence-based support for trauma, anxiety, and more.',
    icon: 'Eye',
    short: 'An evidence-based approach that helps your brain reprocess and release distressing memories so you can move forward with clarity.',
    intro: 'Eye Movement Desensitization and Reprocessing (EMDR) is an evidence-based therapy that helps your brain process and release distressing experiences. If you feel triggered by the past, overwhelmed by emotions you can’t control, or stuck in anxiety, self-doubt, or old patterns despite insight, EMDR may help.',
    experiencing: [
      'Triggered by past experiences',
      'Overwhelmed by emotions you can’t control',
      'Stuck in anxiety, fear, or self-doubt',
      'Unable to move forward despite insight',
    ],
    modalities: [
      { title: 'Trauma & PTSD', text: 'Reprocessing distressing memories that still feel present.' },
      { title: 'Anxiety & Panic', text: 'Reducing the emotional charge of past triggers.' },
      { title: 'Negative Self-Beliefs', text: 'Shifting the beliefs about yourself that hold you back.' },
      { title: 'Emotional Distress', text: 'Softening the imprint of hard experiences on daily life.' },
    ],
    outcomes: [
      'Reduced emotional intensity',
      'Increased clarity and confidence',
      'A greater sense of calm and control',
      'A different relationship with what happened',
    ],
    approach: 'EMDR works by helping your brain finish processing what it wasn’t able to at the time. It doesn’t erase your past — it changes how it lives in you.',
    related: ['trauma-therapy', 'anxiety-therapy', 'womens-counseling'],
  },
  {
    slug: 'anxiety-therapy',
    title: 'Anxiety Therapy',
    seoTitle: 'Anxiety Therapy in Chesapeake, VA | Chesapeake Counseling Center',
    seoDesc: 'Anxiety therapy in Chesapeake, VA — support for overthinking, stress, and racing thoughts. Learn tools to feel calm, clear, and in control.',
    icon: 'Wind',
    short: 'Support in understanding and managing anxiety, reducing overthinking, and creating a greater sense of calm and balance.',
    intro: 'Anxiety can feel exhausting. You might be overthinking everything, feeling constantly on edge, struggling to relax even when things look fine, or noticing racing thoughts and physical tension. You may be functioning — but at a real cost.',
    experiencing: [
      'Overthinking everything',
      'Feeling constantly on edge',
      'Struggling to relax even when things are fine',
      'Racing thoughts or physical tension',
    ],
    modalities: [
      { title: 'Cognitive Behavioral Therapy', text: 'Restructure the thought patterns fueling anxiety.' },
      { title: 'Mindfulness & Grounding', text: 'Return to the present moment when your mind spins.' },
      { title: 'Nervous System Regulation', text: 'Calm the stress response in your body.' },
      { title: 'Pattern Shifting', text: 'Identify and gently shift the roots of anxious patterns.' },
    ],
    outcomes: [
      'Feel calmer and more in control',
      'Reduce overthinking and worry',
      'Improve sleep and focus',
      'Build confidence in yourself',
    ],
    approach: 'We don’t just help you cope — we help you understand what’s driving your anxiety so you can create lasting change.',
    related: ['trauma-therapy', 'emdr-therapy', 'stress-burnout'],
  },
  {
    slug: 'narcissistic-abuse-recovery',
    title: 'Narcissistic Abuse Recovery',
    seoTitle: 'Narcissistic Abuse Recovery Therapy in Chesapeake, VA',
    seoDesc: 'Recover from emotional abuse and toxic relationships. Therapy in Chesapeake, VA focused on healing, boundaries, and rebuilding your sense of self.',
    icon: 'HeartHandshake',
    short: 'Rebuild self-worth, set healthy boundaries, and heal from emotionally damaging relationships.',
    intro: 'If you’ve been in a relationship where you felt controlled, invalidated, or constantly questioning yourself, you are not alone. Narcissistic abuse can leave you confused, drained, struggling with self-worth, or stuck in cycles you can’t seem to break. You may know something wasn’t right, yet still feel pulled back into the pattern.',
    experiencing: [
      'Confusion and second-guessing your reality',
      'Emotional exhaustion and overwhelm',
      'Struggles with self-worth and identity',
      'Cycles you can’t seem to break',
    ],
    modalities: [
      { title: 'Understanding Patterns', text: 'Recognizing patterns of emotional abuse.' },
      { title: 'Rebuilding Identity', text: 'Reconnecting with confidence, values, and voice.' },
      { title: 'Setting Boundaries', text: 'Boundaries that protect your peace and clarity.' },
      { title: 'Emotional Processing', text: 'Working through the impact so it no longer defines you.' },
    ],
    outcomes: [
      'Feel more grounded and clear',
      'Trust yourself again',
      'Let go of guilt and confusion',
      'Create healthier, more secure relationships',
    ],
    approach: 'Healing from narcissistic abuse isn’t only about leaving a relationship — it’s about reconnecting with yourself.',
    related: ['trauma-therapy', 'womens-counseling', 'individual-therapy'],
  },
  {
    slug: 'womens-counseling',
    title: 'Women’s Counseling',
    seoTitle: 'Women’s Counseling in Chesapeake, VA | Therapy for Women',
    seoDesc: 'Compassionate counseling for women in Chesapeake, VA. Support for stress, identity, life transitions, and emotional wellbeing.',
    icon: 'Flower2',
    short: 'Compassionate support for the unique emotional challenges women face, including stress, identity, and life transitions.',
    intro: 'Women often carry the weight of everyone else’s needs while putting their own well-being last. Our practice offers a safe, supportive space where you can focus on yourself — without guilt.',
    experiencing: [
      'Feeling overwhelmed by responsibilities',
      'Struggling with boundaries in relationships',
      'Navigating life transitions (motherhood, divorce, career changes)',
      'Anxiety, self-doubt, or perfectionism',
      'Recovering from emotional abuse or toxic dynamics',
    ],
    modalities: [
      { title: 'Trauma-Informed Care', text: 'Tailored to women’s unique experiences.' },
      { title: 'EMDR & Somatic Work', text: 'Deep, body-aware approaches for lasting healing.' },
      { title: 'Assertiveness & Boundaries', text: 'Skills to protect your energy and voice.' },
      { title: 'Stress Management', text: 'Nervous system regulation and sustainable rest.' },
      { title: 'Emotional Resilience', text: 'Building self-worth and emotional strength.' },
    ],
    outcomes: [
      'Feel more confident and empowered',
      'Set healthy boundaries without guilt',
      'Process past experiences and move forward',
      'Reconnect with your sense of self',
      'Build a life that feels aligned and fulfilling',
    ],
    approach: 'A warm, women-centered space to slow down, reconnect with yourself, and build tools for a life that feels aligned and sustainable.',
    related: ['anxiety-therapy', 'stress-burnout', 'individual-therapy'],
  },
  {
    slug: 'couples-counseling',
    title: 'Couples Counseling',
    seoTitle: 'Couples Counseling in Chesapeake, VA | Relationship Therapy',
    seoDesc: 'Strengthen communication and reconnect in your relationship. Couples counseling in Chesapeake, VA with compassionate, structured support.',
    icon: 'Users',
    short: 'Improve communication, resolve conflict, and build stronger, healthier connections with the people you love.',
    intro: 'Relationships can feel disconnected, tense, or overwhelming — even when both partners want things to be better. Couples counseling provides a structured, supportive space where you can work through challenges together.',
    experiencing: [
      'Communication breakdowns',
      'Trust concerns or rebuilding after betrayal',
      'Difficulty managing conflict constructively',
      'Emotional distance and disconnection',
    ],
    modalities: [
      { title: 'Gottman Method', text: 'Research-based approach for stronger relationships.' },
      { title: 'Emotional Connection', text: 'Rebuild emotional intimacy and trust.' },
      { title: 'Conflict Resolution', text: 'Learn to navigate disagreements constructively.' },
    ],
    outcomes: [
      'Improve communication and understanding',
      'Strengthen emotional connection',
      'Navigate conflict without escalation',
      'Build a healthier, more fulfilling relationship',
    ],
    approach: 'We create a balanced space where both partners feel heard, using research-based approaches to rebuild connection, trust, and understanding.',
    related: ['individual-therapy', 'relationship-counseling', 'womens-counseling'],
  },
  {
    slug: 'individual-therapy',
    title: 'Individual Therapy',
    seoTitle: 'Individual Therapy in Chesapeake, VA | Chesapeake Counseling Center',
    seoDesc: 'Personalized one-on-one therapy in Chesapeake, VA for adults navigating anxiety, transitions, and growth.',
    icon: 'User',
    short: 'One-on-one support tailored to where you are and where you want to go — with clarity, compassion, and warmth.',
    intro: 'A personalized, integrative approach that blends evidence-based tools with genuine human connection to help you move forward.',
    experiencing: [
      'Feeling stuck even when life looks fine',
      'Recurring patterns you want to shift',
      'Grief, transition, or identity questions',
      'Wanting deeper self-understanding',
    ],
    modalities: [
      { title: 'Integrative Approach', text: 'Blending CBT, ACT, and other evidence-based tools.' },
      { title: 'Holistic Focus', text: 'Care that considers mind, body, and life context.' },
      { title: 'Warm Collaboration', text: 'Therapy that feels human, not clinical.' },
    ],
    outcomes: [
      'Greater self-understanding',
      'Healthier patterns and choices',
      'A sense of forward movement',
      'More ease in daily life',
    ],
    approach: 'A personalized, integrative approach designed around you.',
    related: ['anxiety-therapy', 'womens-counseling', 'trauma-therapy'],
  },
  {
    slug: 'telehealth-therapy',
    title: 'Telehealth Therapy',
    seoTitle: 'Telehealth Therapy in Virginia | Chesapeake Counseling Center',
    seoDesc: 'Secure online therapy throughout Virginia. Same warm, personalized care from the comfort of home.',
    icon: 'Video',
    short: 'Secure, convenient online therapy available throughout Virginia — same warmth, from wherever you are.',
    intro: 'HIPAA-secure video sessions make it easy to receive quality care without leaving home, with the same personalized approach as in-person therapy.',
    experiencing: [
      'A busy schedule that makes commuting hard',
      'A preference for therapy from home',
      'Living outside of Chesapeake but within Virginia',
      'Wanting continuity through travel or transitions',
    ],
    modalities: [
      { title: 'Secure Video Sessions', text: 'HIPAA-compliant platform designed for therapy.' },
      { title: 'Same Care, From Home', text: 'The same evidence-based approach as in-person sessions.' },
      { title: 'Statewide Access', text: 'Available throughout Virginia.' },
    ],
    outcomes: [
      'Consistent care around your schedule',
      'Comfort of sessions from home',
      'Access to specialized support',
    ],
    approach: 'Warm, personalized therapy — wherever you are in Virginia.',
    related: ['individual-therapy', 'anxiety-therapy', 'womens-counseling'],
  },
  {
    slug: 'stress-burnout',
    title: 'Stress & Burnout',
    seoTitle: 'Stress & Burnout Therapy in Chesapeake, VA',
    seoDesc: 'Recover from chronic stress and burnout with supportive therapy in Chesapeake, VA. Regulate your nervous system and reclaim rest.',
    icon: 'BatteryLow',
    short: 'Recover from chronic stress and burnout by regulating your nervous system and reclaiming rest.',
    intro: 'Burnout doesn’t mean you failed — it means you’ve been carrying too much for too long. Therapy can help you understand what’s happening in your body and mind, and rebuild capacity in ways that last.',
    experiencing: [
      'Exhaustion that sleep doesn’t fix',
      'Feeling numb, cynical, or disconnected',
      'Trouble concentrating and finishing tasks',
      'A sense of running on empty',
    ],
    modalities: [
      { title: 'Nervous System Regulation', text: 'Understand and calm your stress physiology.' },
      { title: 'Boundaries & Capacity', text: 'Rebuild sustainable rhythms.' },
      { title: 'Rest & Restoration', text: 'Reclaim genuine, restorative rest.' },
    ],
    outcomes: [
      'More energy and clarity',
      'Sustainable pace and boundaries',
      'A restored sense of aliveness',
    ],
    approach: 'We help you understand the physiology of burnout and rebuild capacity through boundaries, regulation, and intentional restoration.',
    related: ['anxiety-therapy', 'high-functioning-anxiety', 'womens-counseling'],
  },
  {
    slug: 'high-functioning-anxiety',
    title: 'High-Functioning Anxiety',
    seoTitle: 'High-Functioning Anxiety Therapy in Chesapeake, VA',
    seoDesc: 'Support for high-functioning anxiety in Chesapeake, VA. For those who look composed but quietly run on internal pressure.',
    icon: 'Sparkles',
    short: 'For those who look composed on the outside while quietly running on anxiety and self-pressure.',
    intro: 'You look like you’re holding it all together on the outside — but inside there’s a constant hum of pressure, perfectionism, and self-criticism. High-functioning anxiety is often invisible to others, and it can be exhausting to carry.',
    experiencing: [
      'Constant inner urgency and pressure',
      'Perfectionism and fear of falling behind',
      'Trouble truly resting or being present',
      'Success that doesn’t feel like enough',
    ],
    modalities: [
      { title: 'Insight-Based Work', text: 'Understand the origins of the pressure.' },
      { title: 'Nervous System Care', text: 'Gently soften the constant activation.' },
      { title: 'Sustainable Boundaries', text: 'Protect your energy without guilt.' },
    ],
    outcomes: [
      'A calmer, more grounded pace',
      'Less internal pressure',
      'Greater self-compassion',
    ],
    approach: 'A grounded, insight-based approach that helps you loosen the grip of internal pressure and cultivate a calmer, more sustainable pace.',
    related: ['anxiety-therapy', 'stress-burnout', 'individual-therapy'],
  },
  {
    slug: 'relationship-counseling',
    title: 'Relationship Counseling',
    seoTitle: 'Relationship Counseling in Chesapeake, VA',
    seoDesc: 'Relationship counseling in Chesapeake, VA for couples, partners, and individuals seeking healthier connection.',
    icon: 'HeartPulse',
    short: 'For couples, partners, and individuals navigating relationship challenges with clarity and care.',
    intro: 'A collaborative, non-judgmental space to explore relational dynamics and build the skills needed for healthier connection.',
    experiencing: [
      'Patterns of conflict or shutdown',
      'Grief around a relationship',
      'Rebuilding after a rupture',
      'Preparing for a new life stage together',
    ],
    modalities: [
      { title: 'Attachment-Informed', text: 'Understand how you connect and why.' },
      { title: 'Communication Skills', text: 'Practical tools for real conversations.' },
      { title: 'Repair & Rebuilding', text: 'Rebuild trust and safety over time.' },
    ],
    outcomes: [
      'Deeper understanding of your patterns',
      'Skills to communicate with less reactivity',
      'A clearer sense of what you want and need',
    ],
    approach: 'A collaborative space for exploring relational dynamics and building healthier connection.',
    related: ['couples-counseling', 'individual-therapy', 'womens-counseling'],
  },
];

export const THERAPISTS = [
  {
    slug: 'jodi-baker-hutchinson',
    name: 'Jodi S. Baker-Hutchinson',
    credentials: 'LCSW',
    role: 'Founder | Licensed Clinical Social Worker | Psychotherapist | EMDR/ART Therapist',
    photo: '/therapists/jodi.jpg',
    quote: 'Healing happens when we learn to approach ourselves with compassion and curiosity instead of criticism.',
    bioShort: 'Jodi is the founder of Chesapeake Counseling Center — an LCSW, EMDR & ART therapist with 10+ years of experience helping clients heal from trauma, anxiety, depression, and life transitions through a holistic mind-body-spirit approach.',
    bioLong: [
      'For more than 10 years, I have been passionate about helping people heal from trauma, anxiety, depression, relationship challenges, and life transitions. I believe every person has an incredible capacity for growth, resilience, and healing when provided with the right support, tools, and compassionate guidance.',
      'Whether you’re feeling overwhelmed, disconnected from yourself, struggling with the effects of childhood experiences, or simply searching for greater purpose and peace, my goal is to provide a safe, supportive space where meaningful change can occur.',
      'My Approach: My approach is holistic, recognizing the powerful connection between the mind, body, emotions, and spirit. Healing isn’t simply about reducing symptoms — it’s about creating a life that feels authentic, peaceful, and aligned with your deepest values.',
      'I integrate evidence-based therapies including EMDR (Eye Movement Desensitization and Reprocessing), Accelerated Resolution Therapy (ART), Cognitive Behavioral Therapy (CBT), Existential Therapy, Internal Family Systems, and Mindfulness and holistic wellness approaches. Together, we’ll work to identify limiting beliefs, process painful experiences, strengthen emotional resilience, and help you reconnect with your own inner light, wisdom, and strength.',
      'My Story: Although my undergraduate degree was in business, I always felt drawn toward understanding people rather than profits. I knew I wanted to dedicate my life to helping others heal. My journey eventually led me to earn my Master’s Degree in Social Work and become a Licensed Clinical Social Worker.',
      'Along the way, I traveled throughout Bali, Indonesia, Costa Rica, Mexico, and Australia, where I immersed myself in different cultures and was deeply inspired by Eastern philosophies emphasizing mindfulness, humility, balance, compassion, and self-awareness. These experiences profoundly shaped my perspective on healing and continue to influence the way I work with clients today.',
      'In addition to working with clients, I have the privilege of leading Chesapeake Counseling Center and mentoring a team of compassionate therapists who share the same commitment to providing exceptional, evidence-based care for our community. I am also a Board-approved Clinical Supervisor with the Virginia Board of Social Work and NASW, offering supervision for aspiring LCSWs across the state.',
      'My Philosophy: I believe healing happens when we learn to approach ourselves with compassion and curiosity instead of criticism. I believe our greatest struggles often become the catalyst for our greatest growth. I believe every person deserves to feel seen, heard, accepted, and empowered. Above all, I believe that no matter what you’ve experienced, hope is always possible.',
      'My faith reminds me daily to choose courage over fear, grace over judgment, and gratitude over despair. While I honor and respect every client’s personal beliefs and values, these principles guide the compassion, authenticity, and encouragement I strive to bring into every session. If you’re ready to begin your healing journey, I would be honored to walk alongside you.',
    ],
    approach: 'Holistic and integrative — honoring the mind-body-emotion-spirit connection through EMDR, Accelerated Resolution Therapy (ART), CBT, Existential Therapy, Internal Family Systems (IFS), and mindfulness-based practices.',
    specialties: [
      'Trauma & PTSD',
      'EMDR Therapy',
      'Accelerated Resolution Therapy (ART)',
      'Anxiety & Depression',
      'Childhood Trauma',
      'Women’s Mental Health',
      'Life Transitions',
      'Internal Family Systems (IFS)',
      'Existential Therapy & CBT',
      'Clinical Supervision (VA)',
    ],
  },
  {
    slug: 'shelby-dillingham',
    name: 'Shelby Dillingham',
    credentials: 'Ph.D., LPC, M.S. Ed.',
    role: 'Psychotherapist',
    photo: '/therapists/Shelby.jpg',
    quote: 'Whether you’re new to therapy or have previous experience with outpatient counseling, I’m grateful to be part of your journey.',
    bioShort: 'Shelby is a Licensed Professional Counselor with a doctorate in Counselor Education, integrating CBT, ACT, the Gottman Method, and Family Systems through a trauma-informed lens for clients of all ages.',
    bioLong: [
      'Hi there, and welcome. Whether you’re new to therapy or have previous experience with outpatient counseling, I’m grateful to be part of your journey.',
      'I have been providing outpatient counseling services for approximately four years while also gaining experience in acute care settings, including inpatient psychiatric centers, hospitals, and primary care practices.',
      'I earned my Master’s degree in Clinical Mental Health Counseling from Old Dominion University in 2019 and completed my Doctorate in Counselor Education in 2024.',
      'My therapeutic approach incorporates evidence-based treatments while also recognizing the important connection between the mind and body. Because every client brings a unique background and set of experiences, I emphasize a collaborative approach with individualized treatment planning tailored to your specific needs.',
      'I integrate several therapeutic modalities into my work, including Cognitive Behavioral Therapy (CBT), Acceptance and Commitment Therapy (ACT), Family Systems Therapy, Motivational Interviewing, and the Gottman Method — all through a trauma-informed lens.',
      'I have worked with a variety of presenting concerns, including depression, anxiety, trauma, infidelity, obsessive compulsive disorder, substance use, thought disorders, and behavioral challenges.',
      'My experience includes serving children ages 7 and older, adolescents, adults, couples, and families. I am LGBTQIA+ affirming and enjoy working with individuals from all communities. Much of my work focuses on understanding how individuals function within larger systems — such as family, work, and relationships — and how those systems can work together more effectively.',
      'When I’m not working, I enjoy drinking coffee, running, reading, caring for my plants, and taking naps.',
    ],
    approach: 'Evidence-based and trauma-informed — integrating CBT, ACT, Family Systems Therapy, Motivational Interviewing, and the Gottman Method, tailored to each client’s unique background and goals.',
    specialties: ['Depression', 'Anxiety', 'Trauma', 'Couples & Family', 'Infidelity', 'OCD', 'Substance Use', 'LGBTQIA+ Affirming', 'Children (7+) & Adolescents'],
  },
  {
    slug: 'jennifer-albritton',
    name: 'Jennifer Albritton',
    credentials: 'LCSW',
    role: 'Psychotherapist',
    photo: 'https://customer-assets.emergentagent.com/job_3fee5f50-b714-4606-9974-a9e2d6965096/artifacts/kzqmme1b_JenniferA_Pic.jpg',
    quote: 'Lay down what you’ve been carrying and reconnect with the parts of yourself that may have been overlooked or stretched thin.',
    bioShort: 'Jennifer supports individuals navigating seasons of transition, identity shifts, military life, reintegration, and the weight of responsibilities that feel heavier than they used to.',
    bioLong: [
      'I support individuals who are navigating seasons of transition, identity shifts, and the weight of responsibilities that feel heavier than they used to. Many of the people I work with are connected to military life, reintegration, court-involved systems, or major changes in relationships and family roles. I approach therapy as a grounded, culturally responsive space where you can lay down what you’ve been carrying and reconnect with the parts of yourself that may have been overlooked or stretched thin.',
      'My practice is collaborative and holistic. I take time to understand the coping strategies, cultural practices, spiritual grounding, and lived wisdom you already bring with you. When those practices are still serving you, we honor and strengthen them. When they no longer feel aligned with who you’re becoming, we explore new tools that support clarity, emotional regulation, and sustainable change. I believe healing is most powerful when it respects the whole person — mind, body, spirit, and community.',
      'I have a special interest in supporting clients through major life transitions, military-related stress, re-entry after incarceration, and the complexities of court-ordered treatment. I also enjoy working with individuals who are seeking a deeper sense of purpose, alignment, or spiritual connection as part of their healing journey. Whether you’re feeling overwhelmed, stuck, or simply ready for something different, I walk alongside you with curiosity, compassion, and a commitment to helping you move at your own pace.',
      'If you’re looking for a therapist who values collaboration, cultural humility, and the integration of spiritual or grounding practices when meaningful to you, I would be honored to support you.',
    ],
    approach: 'Collaborative, holistic, and culturally responsive — honoring the coping strategies, cultural practices, spiritual grounding, and lived wisdom clients already bring with them.',
    specialties: ['Life Transitions', 'Military Life & Reintegration', 'Court-Involved Systems', 'Identity & Purpose', 'Spiritual Integration'],
  },
  {
    slug: 'satrina-artis',
    name: 'Satrina Artis',
    credentials: 'LPC',
    role: 'Psychotherapist',
    photo: '/therapists/satrina.jpg',
    quote: 'Every step forward, no matter how small, is a victory.',
    bioShort: 'Satrina is a Licensed Professional Counselor with 18+ years of clinical experience, a Certified Grief Professional, and a Crisis Intervention Trainer (CIT). She integrates CBT, DBT, and REBT to help clients of all ages heal from a holistic perspective.',
    bioLong: [
      'Satrina Artis is a Licensed Professional Counselor (LPC) in the state of Virginia. Satrina’s approach to therapy is based on the foundations of Cognitive Behavioral Therapy, Dialectical Behavior Therapy, and Rational Emotive Behavior Therapy. She specializes in general issues to include depression, anxiety, trauma, ADHD, behavioral issues (ODD, Conduct Disorder), mood disorders, crisis intervention, life stressors, and grief, anger, impulsivity, and communication/social skills.',
      'She has worked with a wide population of clients — including children, adolescents, and adults — and she is passionate about helping her clients through motivation, identifying their personal strengths, and setting achievable goals from a holistic perspective.',
      'Satrina has a Bachelor’s Degree in Sociology with a minor in Criminal Justice from Norfolk State University and a Master’s degree in Community Counseling from Norfolk State University. She completed her practicum, internship, and residency experience at Western Tidewater Community Services Board conducting crisis and individual counseling, and conducting Virginia preadmission screening for individuals requiring review for acute psychiatric hospitalization.',
      'She has over 18 years of clinical experience in the field, and her previous experience includes Adult Probation & Parole, intensive in-home counseling, therapeutic day treatment, outpatient therapy, substance abuse treatment, children’s mobile crisis services, and mental health support services. Currently, Satrina is a Crisis Intervention Trainer (CIT) and is a Certified Grief Professional.',
      'Satrina is the mother of one child and enjoys spending time with her family. In her spare time, she enjoys cooking, relaxing on the beach, binge-watching series on Netflix, and “striking” on the bowling lanes.',
    ],
    approach: 'Holistic and strengths-based — grounded in Cognitive Behavioral Therapy (CBT), Dialectical Behavior Therapy (DBT), and Rational Emotive Behavior Therapy (REBT), with a focus on motivation, personal strengths, and achievable goals.',
    specialties: ['Anxiety', 'Depression', 'Trauma', 'Grief', 'ADHD', 'Mood Disorders', 'Behavioral Issues (ODD, Conduct)', 'Crisis Intervention', 'Substance Use', 'Children, Adolescents & Adults'],
  },
  {
    slug: 'michelle-chapman',
    name: 'Michelle Chapman',
    credentials: 'MSW, Supervisee in Social Work',
    role: 'Psychotherapist',
    photo: '/therapists/Michelle.jpg',
    quote: 'I would be honored to walk alongside you on a restorative journey toward greater self-understanding, healing, and authenticity.',
    bioShort: 'Michelle graduated with a Master of Social Work from Boston University and takes a trauma-informed, humanistic approach — engaging body, mind, and spirit through ACT, CBT, mindfulness, somatic practices, and Accelerated Resolution Therapy (ART).',
    bioLong: [
      'From Michelle: Life is both beautiful and difficult. Painful experiences can leave us feeling stuck, disconnected, or overwhelmed, diminishing our capacity to navigate life’s challenges and enjoy life’s simple pleasures. We could all use a little extra support from time to time — someone to journey with us through the messiness of life, help us gain perspective, shift patterns, develop new skills, live more authentically, and reconnect with what matters most to us. Perhaps I can support you in this way.',
      'My approach: I strive to create a space where you feel accepted, understood, and able to be fully yourself. My approach is warm, down-to-earth, curious, and collaborative, incorporating your strengths, values, beliefs, and comfort level into our work together. I also believe therapy is most effective when it includes all of you — body, mind, and spirit!',
      'I hold a bachelor’s degree with dual majors in Psychology and Studio Art and a Master of Social Work (MSW). I am currently a Supervisee in Social Work working toward clinical licensure. My professional background includes life and spiritual coaching, nonprofit administration, and social work case management. As a wife and mother of two adult children, I also understand the complexities of balancing relationships, family, work, and caring for ourselves.',
      'I primarily work with adults (25+) experiencing anxiety, stress, trauma, life transitions, relationship issues, difficulties with self-acceptance, and women’s experiences. I draw primarily from Acceptance and Commitment Therapy (ACT), along with compassion-focused, mindfulness, and somatic approaches. I am also trained in Accelerated Resolution Therapy (ART), which I use to support trauma recovery and address a range of mental health concerns.',
    ],
    approach: 'Trauma-informed and humanistic — engaging body, mind, and spirit through ACT, CBT, mindfulness, somatic practices, compassion-focused therapies, and Accelerated Resolution Therapy (ART).',
    specialties: ['Trauma & ART', 'Anxiety', 'Grief & Loss', 'Life Transitions', 'ACT & CBT', 'Mindfulness & Somatic Practices'],
  },
  {
    slug: 'bonnie-murphy',
    name: 'Bonnie Murphy',
    credentials: 'M.A., Resident in Counseling',
    role: 'Psychotherapist',
    photo: '/therapists/Bonnie.jpg',
    quote: 'A strong therapeutic relationship is the foundation for meaningful growth.',
    bioShort: 'Bonnie completed her MA in Clinical Mental Health Counseling at Regent University and brings over three years of clinical experience with adolescents in acute inpatient settings, alongside a growing focus on adult women, bariatric counseling, and the autism spectrum.',
    bioLong: [
      'Seeking counseling is a courageous and meaningful step toward recognizing that you don’t have to face life’s challenges alone. You may be feeling overwhelmed, stuck, misunderstood, or simply ready for something to change. Whatever brings you to therapy, you don’t have to have everything figured out beforehand.',
      'Bonnie believes therapy is most effective when it feels supportive, comfortable, and authentic. While she takes clients’ concerns seriously, she also brings warmth and a sense of humor into the process to help make the journey feel a little lighter. Her goal is to create a space where clients feel seen, understood, encouraged, and empowered to grow at their own pace.',
      'Her approach is tailored to each client’s individual needs and grounded in holistic, strengths-based care. She provides neurodiversity-affirming therapy, with a growing focus on supporting females with AuDHD and those who discover or receive a diagnosis later in life. Additional areas of focus include the emotional and psychological challenges associated with obesity, weight-related concerns, and bariatric care.',
      'Outside of therapy, Bonnie enjoys spending time with her family, her two mischievous huskies, reading, watching scary movies, thrifting for unique treasures, and enjoys being outdoors during cool, overcast days of fall. Fall reminds her of the beauty of transformation, the importance of balance, and, of course, gives her an excuse to live in hoodies. Wherever you may be in your own season of change, Bonnie would be honored to walk alongside you.',
    ],
    approach: 'Holistic, strengths-based, and neurodiversity-affirming — tailored to each client’s individual needs, with a growing focus on females with AuDHD, later-in-life diagnosis, and bariatric care.',
    specialties: ['Neurodiversity-Affirming Care', 'Females with AuDHD', 'Later-in-Life Diagnosis', 'Bariatric & Weight-Related Care', 'Anxiety', 'Life Transitions'],
  },
  {
    slug: 'krystal-casper',
    name: 'Krystal Casper',
    credentials: 'M.A., Resident in Counseling',
    role: 'Psychotherapist',
    photo: 'https://customer-assets-4nw71qhi.emergentagent.net/job_3e895b52-9bb3-4be4-a634-0c8f65904c76/artifacts/zfjl1rw9_Krystal.jpeg',
    quote: 'Effective therapy begins with a strong therapeutic relationship built on trust, acceptance, and genuine understanding.',
    bioShort: 'Krystal is a Resident in Counseling in Virginia who helps adolescents and adults navigate life’s challenges through an integrative approach drawing from CBT, DBT, and person-centered principles — with a long-term goal of specializing in trauma.',
    bioLong: [
      'Krystal is a Resident in Counseling in Virginia with a passion for helping adolescents and adults navigate life’s challenges, better understand themselves, and create meaningful change. Originally from Mississippi, she spent the last 15 years in Louisiana before recently relocating to Virginia. Krystal earned her bachelor’s degree in Psychology from Louisiana State University and her master’s degree in Clinical Mental Health Counseling from Liberty University. She is also continuing her education at the doctoral level, with a long-term goal of specializing in trauma.',
      'Krystal’s approach to counseling is integrative and individualized, drawing primarily from Cognitive Behavioral Therapy (CBT), Dialectical Behavior Therapy (DBT), and person-centered principles. She believes effective therapy begins with a strong therapeutic relationship built on trust, acceptance, and genuine understanding. She works collaboratively with clients to recognize patterns, develop practical coping skills, build confidence, and find healthier ways to navigate difficult emotions and life circumstances.',
      'Krystal works with clients experiencing anxiety, depression, self-esteem concerns, relationship challenges, and life transitions. She values the opportunity to support adolescents and young adults as they navigate the challenges and changes that come with these formative stages of life, as well as individuals and families adjusting to transitions such as relocation, changing roles, and the demands of military life. She strives to create a supportive environment where clients feel comfortable being open and exploring their experiences at their own pace.',
      'As she continues to grow as a clinician, Krystal is especially interested in expanding her knowledge and training in trauma and understanding how past experiences can influence the way we think, feel, and relate to others.',
    ],
    approach: 'Integrative and individualized — drawing primarily from Cognitive Behavioral Therapy (CBT), Dialectical Behavior Therapy (DBT), and person-centered principles, grounded in a strong therapeutic relationship built on trust, acceptance, and genuine understanding.',
    specialties: ['Anxiety', 'Depression', 'Self-Esteem', 'Relationship Challenges', 'Life Transitions', 'Adolescents & Young Adults', 'Military Life & Families', 'CBT & DBT'],
  },
  {
    slug: 'jimmy-bach',
    name: 'Jimmy Bach',
    credentials: 'M.A., Resident in Counseling',
    role: 'Psychotherapist',
    photo: 'https://customer-assets.emergentagent.com/job_3fee5f50-b714-4606-9974-a9e2d6965096/artifacts/9ygtod9q_Jimmy%20Pic.jpeg',
    quote: 'Each person’s journey is uniquely their own, yet no one should have to walk difficult paths alone.',
    bioShort: 'Jimmy brings 32 years of Navy service, a faith-grounded perspective, and a person-centered, strengths-based, trauma-informed approach to his work with clients.',
    bioLong: [
      'Jimmy holds a BS in Communication from Old Dominion University, an MS in Leadership from Central Michigan University, and an MA in Clinical Mental Health Counseling from Liberty University. He utilizes a person-centered, strengths-based, and trauma-informed integrated approach in his work with clients. He is committed to honoring each client’s inherent dignity and treating all clients with unwavering respect, free from judgment, as they navigate their personal journeys toward understanding and growth.',
      'Jimmy’s professional background has served as a catalyst for his transition into the mental health field, where he approaches counseling with both compassion and practical insight into human behavior and resilience. He retired from military service after 32 years of active duty in the US Navy and with him he brought a wealth of leadership experience and an understanding of military life and its challenges.',
      'Originally from Long Island, New York, Jimmy draws strength from his upbringing as the fourth of six children in a loving Christian family. His path to counseling emerged from personal experiences that revealed to him the transformative power of compassionate support during difficult times. Recognizing the profound impact that skilled counseling can have, he felt called to "pay it forward" by dedicating his next chapter to supporting others through their own challenges.',
      'As a husband of over 26 years and father to two teenagers, Jimmy understands the complexities of family dynamics and the importance of balancing multiple life roles. His approach to counseling is informed by his faith journey, military experience, and the perspective gained from raising a family while serving his country. Jimmy’s commitment to helping others extends beyond his professional aspirations, reflecting a genuine desire to make a meaningful difference in people’s lives.',
    ],
    vision: 'I believe that each person’s journey is uniquely their own, yet no one should have to walk difficult paths alone. My approach to counseling is grounded in my faith and informed by my experiences in military service, family life, and personal growth. I am committed to creating a safe, respectful space where clients can explore their challenges, discover their strengths, and work toward meaningful change. Drawing from my own life experiences, I understand that healing and growth often require both compassion and practical strategies. I view the counseling relationship as a collaborative partnership, where my role is to walk alongside clients, offering support, insights, and evidence-based techniques while honoring each person’s capacity for resilience and self-determination. I believe that true transformation happens when we address not just symptoms but underlying patterns, and when we consider the whole person — mind, body, and spirit. My goal is to help clients not only overcome immediate challenges but develop lasting skills for continued growth and well-being.',
    approach: 'Person-centered, strengths-based, and trauma-informed integrated approach — honoring each client’s inherent dignity with unwavering respect and no judgment.',
    specialties: [
      'Addiction & Recovery',
      'Military Service Members & Veterans',
      'Military Families & Transitions',
      'First Responders',
      'Life Transitions & Purpose',
      'Family Dynamics',
      'Neurodivergence',
      'Stress Management',
      'Grief & Loss',
      'Personal Growth & Resilience',
      'Career Counseling',
      'Premarital Counseling',
      'Couples',
    ],
  },
  {
    slug: 'jill-kivikoski',
    name: 'Jill Kivikoski',
    credentials: 'Ph.D., Resident in Counseling',
    role: 'Psychotherapist',
    photo: 'https://customer-assets.emergentagent.com/job_3fee5f50-b714-4606-9974-a9e2d6965096/artifacts/m0oaudjn_Jill_Pic.png',
    quote: 'Healing happens through authentic human connection.',
    bioShort: 'Jill brings more than ten years of behavioral health experience across community mental health, telehealth, and integrated care — with a trauma-informed, culturally responsive practice.',
    bioLong: [
      'As a Resident in Counseling in Virginia, I provide compassionate, collaborative counseling for adults navigating anxiety, depression, trauma, stress, relationship challenges, life transitions, and other emotional concerns. With more than ten years of experience in behavioral health across community mental health, telehealth, and integrated care settings, I bring both clinical expertise and genuine empathy to the counseling relationship.',
      'My approach is trauma-informed, culturally responsive, and grounded in the belief that healing happens through authentic human connection. I draw from humanistic, interpersonal, and evidence-based approaches to help clients better understand themselves, build healthier coping strategies, strengthen relationships, and move toward meaningful change.',
      'I understand that reaching out for support can feel vulnerable, especially during times of uncertainty or overwhelm. My goal is to create a safe, supportive space where clients feel heard, respected, and empowered to grow at their own pace.',
      'Whether you are working through difficult emotions, adjusting to life changes, processing past experiences, or simply feeling stuck, therapy can offer insight, healing, and renewed direction. I consider it a privilege to walk alongside clients as they work toward greater well-being and a more fulfilling life.',
    ],
    approach: 'Trauma-informed, culturally responsive, and grounded in the belief that healing happens through authentic human connection — drawing from humanistic, interpersonal, and evidence-based approaches.',
    specialties: ['Anxiety', 'Depression', 'Trauma', 'Stress', 'Relationships', 'Life Transitions'],
  },
  {
    slug: 'cassie-marsh',
    name: 'Cassie Marsh',
    credentials: 'MSW Intern',
    role: 'Psychotherapist',
    photo: 'https://customer-assets.emergentagent.com/job_3fee5f50-b714-4606-9974-a9e2d6965096/artifacts/ixlks0m4_Cassie-Pic.webp',
    quote: 'You already possess the resilience and abilities needed to create meaningful, positive change.',
    bioShort: 'Cassie is an MSW Intern in Individual and Family Clinical Practice with a solution-focused, strength-based approach — passionate about supporting children, teens, and first-responder & military families.',
    bioLong: [
      'Cassie is a Master of Social Work Intern pursuing her MSW in Individual and Family Clinical Practice at Liberty University, with the goal of becoming a Licensed Clinical Social Worker (LCSW).',
      'Cassie’s counseling approach is primarily solution-focused and strength-based, grounded in the belief that individuals already possess the resilience and abilities needed to create meaningful, positive change. She collaborates with clients to identify personal strengths, build on existing successes, and develop practical, goal-oriented solutions.',
      'She also integrates Cognitive Behavioral Therapy (CBT) techniques to help clients recognize and challenge unhelpful thought patterns while developing healthy coping skills. At the client’s request, spiritual and faith-based counseling can be incorporated in a way that aligns with their personal values and beliefs.',
      'Cassie has experience supporting individuals impacted by trauma and substance abuse and is passionate about creating a compassionate, supportive environment where clients feel empowered to grow, heal, and thrive.',
      'Her clinical interests include working with children, teens, young adults, first responder families, military families, and individuals navigating life transitions. She is especially interested in trauma-informed care, addiction and recovery, family dynamics, resilience building, and supporting clients through seasons of change and healing. She is particularly passionate about helping children build confidence, develop a strong sense of identity, and feel less alone as they navigate the process of discovering who they are.',
      'Cassie is also a law enforcement spouse and mother of four, and she brings over 14 years of experience working in a ministry office setting, which has shaped her understanding of service, resilience, and supporting individuals and families through both everyday and complex life challenges.',
    ],
    approach: 'Solution-focused and strength-based, integrating Cognitive Behavioral Therapy (CBT); faith-based counseling available at the client’s request.',
    specialties: [
      'Children, Teens & Young Adults',
      'First Responder Families',
      'Military Families',
      'Trauma-Informed Care',
      'Addiction & Recovery',
      'Family Dynamics',
      'Life Transitions',
    ],
  },
];

// Non-therapist staff (Office Manager, admin, etc.) — shown in a separate section on the Team page.
export const OFFICE_STAFF = [
  {
    slug: 'sandi-ayres',
    name: 'Sandi Ayres',
    credentials: '',
    role: 'Client Relations & Scheduling Coordinator',
    photo: '/therapists/Sandi.jpg',
    bioShort: 'Sandi serves as Chesapeake Counseling Center’s Client Relations & Scheduling Coordinator and is often the first friendly voice clients hear when they reach out. With a warm, welcoming approach, she helps guide new and returning clients through scheduling, intake paperwork, and any questions along the way — making the process as smooth and stress-free as possible. Outside the office, Sandi is a business owner, community leader, and current member of the Currituck County Board of Education, with a passion for supporting both local businesses and the well-being of families in our community.',
  },
];

export const TESTIMONIALS = [
  {
    quote: 'I can’t thank Jodi and the Chesapeake Counseling Center enough for the care and compassion she has shown my parents through counseling. Jodi is a genuinely caring counselor who listens with empathy and goes above and beyond to support her clients. Her professionalism, kindness, and dedication have made a meaningful difference for our family.',
    author: 'Client Family Member',
  },
  {
    quote: 'Chesapeake Counseling Center has been a safe place to work through some of my recent challenges. Years ago, when I was seeing a therapist, it was never something I looked forward to. That’s not the case with Jodi. I look forward to my sessions. She asks thoughtful questions that help me get to the root of the problem. I highly recommend her.',
    author: 'Individual Therapy Client',
  },
];

// All blog posts pulled from https://chesapeakecounselingcenter.com/blog/
export const BLOG_POSTS = [
  {
    slug: 'mental-load-why-youre-exhausted-even-at-rest',
    title: 'Mental Load — Why You’re Exhausted Even at Rest',
    author: 'Jimmy Bach, Resident in Counseling',
    date: 'June 2, 2026',
    cover: 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?auto=format&fit=crop&w=1200&q=80',
    tags: ['Mental Load', 'Burnout', 'Emotional Labor'],
    excerpt: 'Feeling mentally exhausted even after resting? Learn how the mental load, emotional labor, and unseen responsibilities quietly drain your energy — and what to do about it.',
    body: [
      'You woke up tired. You went to bed tired. Even a full weekend of rest hasn’t helped. If that sounds familiar, you might be carrying what psychologists call the mental load — the invisible work of remembering, planning, and anticipating on behalf of everyone around you.',
      'Unlike physical tasks, the mental load runs constantly in the background: appointments to schedule, groceries to remember, needs to anticipate. It rarely gets named, but it takes up enormous cognitive and emotional space.',
      'The good news is that naming it is the first step to shifting it. In therapy, we help you notice what you’re carrying, communicate it clearly, and begin sharing or releasing pieces of it so that rest can actually feel restful again.',
    ],
  },
  {
    slug: 'why-anger-is-usually-a-secondary-emotion',
    title: 'Why Anger Is Usually a Secondary Emotion',
    author: 'Jimmy Bach, Resident in Counseling',
    date: 'May 21, 2026',
    cover: 'https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?auto=format&fit=crop&w=1200&q=80',
    tags: ['Emotions', 'Self-Awareness'],
    excerpt: 'Anger often masks something softer underneath. Learn how to identify the primary emotion beneath your anger and respond with more clarity and self-understanding.',
    body: [
      'Anger has a bad reputation, but it’s actually a useful emotion. It tells us something matters — a boundary was crossed, a need is unmet, something feels unfair.',
      'What often gets missed is that anger is usually a secondary emotion. Beneath it, you’ll frequently find hurt, fear, shame, disappointment, or grief.',
      'When you learn to pause and ask, “What’s underneath this?” you begin to communicate what you actually need — rather than reacting from a place of overwhelm.',
    ],
  },
  {
    slug: 'why-you-feel-stuck-even-when-life-looks-fine',
    title: 'Why You Feel Stuck Even When Life Looks Fine',
    author: 'CCC Team',
    date: 'April 15, 2026',
    cover: 'https://images.unsplash.com/photo-1494548162494-384bba4ab999?auto=format&fit=crop&w=1200&q=80',
    tags: ['Life Transitions', 'Self-Discovery'],
    excerpt: 'From the outside, everything looks fine. So why does something quietly feel off? That restlessness has a name — and a way forward.',
    body: [
      'From the outside, life may look good. So why does something quietly feel wrong — a restlessness that shows up in your chest at the end of the day?',
      'Feeling stuck when life looks fine doesn’t mean you’re ungrateful. It usually means something inside you is asking for attention.',
      'Therapy helps you unpack the layers, understand what’s beneath the restlessness, and take small, meaningful steps toward a life that feels like yours.',
    ],
  },
  {
    slug: 'how-to-stop-overthinking-at-night',
    title: 'How to Stop Overthinking at Night',
    author: 'CCC Team',
    date: 'April 10, 2026',
    cover: 'https://images.unsplash.com/photo-1520206183501-b80df61043c2?auto=format&fit=crop&w=1200&q=80',
    tags: ['Anxiety', 'Sleep', 'Overthinking'],
    excerpt: 'The lights are off, the world is quiet — and your brain decides it’s the perfect time to review your entire life. Here’s why, and what actually helps.',
    body: [
      'When your day slows down, your nervous system finally has room to notice what it’s been holding — which is exactly why anxious thoughts often show up at night.',
      'Instead of trying to force your mind to stop, we help clients gently regulate the body first. Slow exhales, grounding, and simple containment practices tell your nervous system it’s safe to rest.',
      'Long term, we look at what your overthinking is trying to protect you from. Once your system feels safer during the day, nights get quieter too.',
    ],
  },
  {
    slug: 'why-high-functioning-anxiety-is-so-exhausting',
    title: 'Why High-Functioning Anxiety Is So Exhausting',
    author: 'CCC Team',
    date: 'April 8, 2026',
    cover: 'https://images.unsplash.com/photo-1544027993-37dbfe43562a?auto=format&fit=crop&w=1200&q=80',
    tags: ['Anxiety', 'Burnout', 'Perfectionism'],
    excerpt: 'You’re meeting every deadline and holding it all together. So why do you feel so tired underneath?',
    body: [
      'High-functioning anxiety often looks like reliability, productivity, and quiet perfectionism. On the inside, it can feel like running on a low-grade alarm all day.',
      'The exhaustion isn’t weakness — it’s the cost of staying “on” for long stretches of time. Your nervous system is doing exactly what it learned to do to keep you safe.',
      'In therapy, we help you understand where the pressure comes from, soften the internal alarm, and build a slower, more sustainable pace.',
    ],
  },
  {
    slug: 'signs-youre-healing-from-narcissistic-abuse',
    title: 'Signs You’re Healing From Narcissistic Abuse',
    author: 'CCC Team',
    date: 'April 5, 2026',
    cover: 'https://images.unsplash.com/photo-1503437313881-503a91226402?auto=format&fit=crop&w=1200&q=80',
    tags: ['Narcissistic Abuse', 'Recovery', 'Boundaries'],
    excerpt: 'Healing doesn’t always look like what you expect. Sometimes the most powerful signs of recovery are the quiet ones.',
    body: [
      'Recovery from narcissistic abuse rarely feels dramatic. Often it looks like being able to disagree without spiraling, sitting with uncomfortable emotions without collapsing, or trusting your own perception again.',
      'These small moments are the real signs of healing. Your nervous system is learning that it’s safe to have needs, boundaries, and a voice.',
      'If you’re noticing any of these shifts — even in small ways — you are healing. And you’re allowed to feel proud of that.',
    ],
  },
  {
    slug: 'what-emdr-therapy-feels-like',
    title: 'What EMDR Therapy Feels Like (A Real Experience)',
    author: 'CCC Team',
    date: 'April 1, 2026',
    cover: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1200&q=80',
    tags: ['EMDR', 'Trauma', 'Therapy Modalities'],
    excerpt: 'EMDR can sound almost too strange to work — moving your eyes to heal trauma? Here’s what it actually feels like in the room.',
    body: [
      'EMDR sessions often begin with resource-building — identifying tools, images, or memories that help you feel grounded and safe before any processing begins.',
      'When processing does happen, most clients describe it as noticing thoughts, feelings, and body sensations shift — sometimes memories become less charged, sometimes new insights arrive.',
      'You stay in control the whole time. EMDR isn’t about reliving the worst moments of your life; it’s about helping your brain finish processing what it couldn’t before.',
    ],
  },
  {
    slug: 'what-does-trauma-feel-like-in-the-body',
    title: 'What Does Trauma Feel Like in the Body?',
    author: 'CCC Team',
    date: 'March 25, 2026',
    cover: 'https://images.unsplash.com/photo-1518241353330-0f7941c2d9b5?auto=format&fit=crop&w=1200&q=80',
    tags: ['Trauma', 'Somatic', 'Nervous System'],
    excerpt: 'Trauma doesn’t just live in your thoughts — it hides in your muscles, your breath, and the smallest moments of your day.',
    body: [
      'Trauma is often stored somatically — in tight shoulders, a clenched jaw, shallow breathing, or a stomach that never quite settles.',
      'This isn’t “all in your head.” It’s your body remembering what it needed to do in order to survive.',
      'Somatic and EMDR-informed therapy help your body finish what it couldn’t at the time — so healing can happen at the level where trauma actually lives.',
    ],
  },
  {
    slug: 'breaking-the-cycle-of-overthinking',
    title: 'Breaking the Cycle of Overthinking: 5 Therapist-Approved Strategies',
    author: 'CCC Team',
    date: 'March 15, 2026',
    cover: 'https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&w=1200&q=80',
    tags: ['Anxiety', 'Overthinking', 'CBT'],
    excerpt: 'Do you replay conversations, worry about things you can’t control, or run through worst-case scenarios? Here’s how to gently shift the pattern.',
    body: [
      'Overthinking often shows up as a way to feel in control when something feels uncertain. Recognizing that is the first shift.',
      'Simple tools like naming the thought, setting a worry window, and grounding through the senses can help interrupt the loop.',
      'Over time, therapy helps you address what’s beneath the overthinking — so the loop has less fuel in the first place.',
    ],
  },
  {
    slug: 'why-setting-boundaries-isnt-selfish',
    title: 'Why Setting Boundaries Isn’t Selfish: A Therapist’s Guide',
    author: 'Chesapeake Counseling Center',
    date: 'March 5, 2026',
    cover: 'https://images.unsplash.com/photo-1508672019048-805c876b67e2?auto=format&fit=crop&w=1200&q=80',
    tags: ['Boundaries', 'Relationships', 'Self-Care'],
    excerpt: 'If you’ve ever agreed to something you didn’t want to do, saying “yes” out of guilt or fear, this one is for you.',
    body: [
      'Boundaries are not walls — they’re a way of telling other people how to be in relationship with you.',
      'Guilt after setting a boundary doesn’t mean you did something wrong. It often means you’re doing something new.',
      'Therapy helps you distinguish between guilt that’s a signal and guilt that’s a habit — so your “no” becomes protective rather than punishing.',
    ],
  },
  {
    slug: 'attachment-styles-in-relationships',
    title: 'Attachment Styles in Relationships: Understanding How You Connect',
    author: 'CCC Team',
    date: 'February 20, 2026',
    cover: 'https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?auto=format&fit=crop&w=1200&q=80',
    tags: ['Relationships', 'Attachment'],
    excerpt: 'We all bring more than we realize into our relationships. Understanding your attachment style can help you show up with more clarity and self-compassion.',
    body: [
      'Attachment styles — secure, anxious, avoidant, disorganized — describe patterns we learned early on for connecting to others.',
      'They’re not a fixed diagnosis. They’re a map that helps you understand your reactions in relationships and where they came from.',
      'With awareness (and support), attachment patterns can shift toward more security and ease over time.',
    ],
  },
  {
    slug: 'what-is-trauma-and-what-tools-help',
    title: 'What Is Trauma? And What Tools Are Most Helpful in Treatment?',
    author: 'CCC Team',
    date: 'February 10, 2026',
    cover: 'https://images.unsplash.com/photo-1470137237906-d8a4f71e1966?auto=format&fit=crop&w=1200&q=80',
    tags: ['Trauma', 'EMDR', 'Somatic'],
    excerpt: 'Trauma means different things to different people. Here’s a compassionate look at what it is — and what actually helps.',
    body: [
      'Trauma isn’t defined by the size of the event; it’s defined by how your nervous system experienced it.',
      'Effective trauma treatment often combines evidence-based tools such as EMDR, somatic work, and cognitive processing — always at a pace that feels safe.',
      'You don’t need to remember everything or share every detail to heal. Real healing happens in the body and in the relationship with your therapist.',
    ],
  },
  {
    slug: 'effective-strategies-to-manage-stress-and-anxiety',
    title: 'Effective Strategies to Manage Stress and Anxiety',
    author: 'CCC Team',
    date: 'January 28, 2026',
    cover: 'https://images.unsplash.com/photo-1476611317561-60117649dd94?auto=format&fit=crop&w=1200&q=80',
    tags: ['Anxiety', 'Stress', 'Coping Skills'],
    excerpt: 'Feeling overwhelmed is common — but that doesn’t mean it has to run your life. A few grounded strategies can go a long way.',
    body: [
      'Regulation before problem-solving: when your nervous system is activated, no strategy will land. Start with the body first.',
      'Slow, extended exhales, gentle movement, and grounding through the senses are simple, powerful ways to shift out of stress mode.',
      'Long-term, therapy helps you identify the deeper patterns fueling stress so you’re not just managing symptoms but reshaping the system.',
    ],
  },
  {
    slug: 'healing-beyond-talk-therapy-emdr',
    title: 'Healing Beyond Talk Therapy: How EMDR Can Transform Trauma',
    author: 'CCC Team',
    date: 'January 20, 2026',
    cover: 'https://images.unsplash.com/photo-1517502884422-41eaead166d4?auto=format&fit=crop&w=1200&q=80',
    tags: ['EMDR', 'Trauma', 'Therapy Modalities'],
    excerpt: 'For many people, insight alone isn’t enough to heal. EMDR offers a different, deeply effective path.',
    body: [
      'Talk therapy is powerful — but sometimes we understand our patterns intellectually and still feel stuck emotionally.',
      'EMDR helps the brain finish processing experiences that were never fully digested, often bringing relief that talk therapy alone can’t reach.',
      'It’s not a shortcut. It’s a different door into the same room — and for many clients, it opens.',
    ],
  },
  {
    slug: 'emdr-vs-art',
    title: 'EMDR vs. ART: Two Powerful Paths to Healing Trauma',
    author: 'CCC Team',
    date: 'January 15, 2026',
    cover: 'https://images.unsplash.com/photo-1552346154-21d32810aba3?auto=format&fit=crop&w=1200&q=80',
    tags: ['EMDR', 'ART', 'Trauma'],
    excerpt: 'Trauma recovery is not one-size-fits-all. Here’s a compassionate look at two effective, research-informed modalities.',
    body: [
      'Both EMDR (Eye Movement Desensitization and Reprocessing) and ART (Accelerated Resolution Therapy) use eye movements to help the brain process distressing experiences.',
      'They’re not the same, though. Each has its own protocol, pace, and areas of strength — which is why it’s helpful to work with a therapist who can meet you where you are.',
      'The right modality is the one that fits your goals, your nervous system, and the kind of care you’re looking for.',
    ],
  },
  {
    slug: 'embracing-grace-and-gratitude',
    title: 'Embracing Grace and Gratitude: A Gentle Path to Emotional Wellness',
    author: 'CCC Team',
    date: 'January 10, 2026',
    cover: 'https://images.unsplash.com/photo-1489533119213-66a5cd877091?auto=format&fit=crop&w=1200&q=80',
    tags: ['Gratitude', 'Grace', 'Wellness'],
    excerpt: 'In the rush of daily life, it’s easy to focus on what’s missing. Grace and gratitude offer a softer place to stand.',
    body: [
      'Grace is what we give ourselves when we’re still learning. Gratitude is what we notice when we slow down enough to see.',
      'Practiced together, they don’t erase what’s hard — they hold it more gently.',
      'Small daily rituals of grace and gratitude, especially when life feels heavy, can be surprisingly powerful anchors.',
    ],
  },
  {
    slug: 'be-kind-to-your-mind',
    title: 'Be Kind to Your Mind Today',
    author: 'CCC Team',
    date: 'January 5, 2026',
    cover: 'https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&w=1200&q=80',
    tags: ['Self-Compassion', 'Mental Health'],
    excerpt: 'Your mind is where your thoughts live and your emotions move. Today, offer it a little more compassion.',
    body: [
      'You wouldn’t talk to a friend the way you talk to yourself on a hard day. Practicing kindness toward your own mind is a small, radical act.',
      'It doesn’t mean ignoring what’s hard — it means responding to yourself with the same care you’d offer someone you love.',
      'Over time, self-compassion becomes its own quiet form of healing.',
    ],
  },
  {
    slug: 'meet-michelle-chapman',
    title: 'Our Team is Growing — Meet Michelle Chapman',
    author: 'CCC Team',
    date: 'December 12, 2025',
    cover: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=1200&q=80',
    tags: ['Team', 'Announcements'],
    excerpt: 'We’re excited to introduce Michelle Chapman, a certified life coach and advanced placement intern joining our team.',
    body: [
      'Michelle Chapman (she/her/hers) holds a B.A. in Psychology and Studio Art and is a Certified Life-Coach.',
      'She joins Chesapeake Counseling Center as an Advanced Placement Intern with a warm, thoughtful presence and a passion for supporting personal growth.',
      'We’re so glad to have Michelle as part of the team.',
    ],
  },
  {
    slug: 'are-you-an-empath',
    title: 'Are You an Empath?',
    author: 'CCC Team',
    date: 'November 20, 2025',
    cover: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&w=1200&q=80',
    tags: ['Empath', 'Self-Awareness'],
    excerpt: 'Being an empath comes with heightened sensitivity — and heightened needs for care. Here’s how to honor both.',
    body: [
      'Empaths often pick up on emotions and energy in ways that others don’t. That sensitivity is a strength — and also something that needs tending.',
      'Without care, empaths can end up carrying feelings that don’t belong to them, leading to overwhelm and burnout.',
      'Therapy helps you understand your sensitivity, build practices for grounding and boundaries, and honor your empath nature without being drained by it.',
    ],
  },
  {
    slug: 'supporting-those-living-with-chronic-pain',
    title: 'Supporting Those Living With Chronic Pain',
    author: 'Nikki Swoope, NYU Intern',
    date: 'November 10, 2025',
    cover: 'https://images.unsplash.com/photo-1512069772995-ec65ed45afd6?auto=format&fit=crop&w=1200&q=80',
    tags: ['Chronic Pain', 'Mental Health'],
    excerpt: 'Chronic pain affects far more than the body — it touches identity, relationships, and hope. Emotional support is essential.',
    body: [
      'For those living with chronic pain, the emotional and psychological toll is often as significant as the physical experience.',
      'Therapy can provide space to process grief, fear, and identity shifts — and to build practical tools for coping.',
      'You are more than your pain. Support can make a real difference in how you carry it.',
    ],
  },
  {
    slug: 'mindfulness-through-grief',
    title: 'Mindfulness: Steps to Heal and Grow Through Grief',
    author: 'Nikki Swoope, NYU Intern',
    date: 'October 30, 2025',
    cover: 'https://images.unsplash.com/photo-1518398046578-8cca57782e17?auto=format&fit=crop&w=1200&q=80',
    tags: ['Grief', 'Mindfulness'],
    excerpt: 'Loss touches all of us. Mindfulness offers a gentle path through — not around — grief.',
    body: [
      'Grief isn’t something to be solved. It’s something to be carried, and sometimes shared.',
      'Mindfulness-based practices help you notice what’s here without needing to change it — which is often what grief truly needs.',
      'With time and support, moving through grief can become a path toward a deeper, more integrated version of yourself.',
    ],
  },
  {
    slug: 'understanding-codependency',
    title: 'Understanding the Complexity of Codependency and Related Dynamics',
    author: 'Nikki Swoope',
    date: 'October 15, 2025',
    cover: 'https://images.unsplash.com/photo-1506863530036-1efeddceb993?auto=format&fit=crop&w=1200&q=80',
    tags: ['Codependency', 'Relationships'],
    excerpt: 'Codependency isn’t about “too much love” — it’s about a pattern of self-abandonment. Here’s a compassionate look at what it is.',
    body: [
      'Codependency often shows up as caretaking that overrides your own needs, or feeling responsible for others’ emotions.',
      'These patterns usually make sense in light of your history — they were once protective.',
      'Therapy helps you understand the roots, honor what those patterns tried to do for you, and gently build new ways of being in relationship.',
    ],
  },
  {
    slug: 'we-are-growing-and-expanding',
    title: 'We Are Growing and Expanding!',
    author: 'CCC Team',
    date: 'September 20, 2025',
    cover: 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1200&q=80',
    tags: ['Announcements'],
    excerpt: 'After 3.5 years in our current office, we’re expanding to serve more of the Hampton Roads community.',
    body: [
      'It has been an honor to grow alongside the clients and community we serve here in Chesapeake, VA.',
      'To meet growing demand, we’re expanding our team and space so we can continue offering timely, compassionate care.',
      'Thank you for being part of this journey. Stay tuned for more updates as we grow.',
    ],
  },
  {
    slug: 'good-faith-estimate',
    title: 'Good Faith Estimate',
    author: 'Chesapeake Counseling Center',
    date: 'August 15, 2025',
    cover: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1200&q=80',
    tags: ['Billing', 'No Surprises Act'],
    excerpt: 'You have the right to receive a Good Faith Estimate explaining how much your medical and mental health care will cost. Here’s what to know.',
    body: [
      'Under the federal No Surprises Act, you have the right to receive a Good Faith Estimate for the expected cost of non-emergency healthcare items and services.',
      'This estimate is provided in advance and helps you plan for the cost of care without hidden surprises.',
      'If you have questions about billing or want to request a Good Faith Estimate, please contact our office — we’re happy to walk you through it.',
    ],
  },
  {
    slug: 'introducing-deborah-jones',
    title: 'Introducing Our Newest Therapist: Deborah Jones, LCSW',
    author: 'Chesapeake Counseling Center',
    date: 'July 30, 2025',
    cover: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=1200&q=80',
    tags: ['Team', 'Announcements'],
    excerpt: 'We’re so excited to welcome Debbie to our team — an LCSW with a deep commitment to compassionate, client-centered care.',
    body: [
      'Debbie Jones, LCSW, joins our practice with years of experience and a warm, thoughtful approach.',
      'She brings a strong foundation in trauma-informed care and a genuine passion for supporting clients through life’s challenges.',
      'Please help us welcome Debbie to Chesapeake Counseling Center.',
    ],
  },
  {
    slug: 'self-reflecting-what-to-let-go',
    title: 'Are You Self-Reflecting? What Do You Need to Let Go of This Year?',
    author: 'CCC Team',
    date: 'June 25, 2025',
    cover: 'https://images.unsplash.com/photo-1495995424756-6a5a3f9e7543?auto=format&fit=crop&w=1200&q=80',
    tags: ['Self-Reflection', 'Growth'],
    excerpt: 'Self-reflection is one of the simplest ways to reconnect with what matters. This year, what are you ready to release?',
    body: [
      'Self-reflection helps us look honestly at what we’re carrying — and what we’re ready to lay down.',
      'Sometimes what needs to be released isn’t a habit but a story we’ve been telling ourselves.',
      'Take a quiet moment, name what you’re ready to let go of, and trust that space you create is space where something new can grow.',
    ],
  },
  {
    slug: 'beauty-is-all-around-you',
    title: 'Beauty Is All Around You — Slow Down to Notice',
    author: 'CCC Team',
    date: 'May 30, 2025',
    cover: 'https://images.unsplash.com/photo-1470252649378-9c29740c9fa8?auto=format&fit=crop&w=1200&q=80',
    tags: ['Mindfulness', 'Presence'],
    excerpt: 'In the quiet moments, clarity begins to arrive. Slow down long enough to see the beauty that’s already here.',
    body: [
      'Beauty is easy to miss when we’re in constant motion. Slowing down — even for a minute — lets it back in.',
      'Try noticing one small thing that feels beautiful to you today: the way light falls, a sound, a breath.',
      'Small moments of presence add up to a life that feels more alive.',
    ],
  },
  {
    slug: 'relax-inhale-exhale',
    title: 'Relax. Inhale. Exhale.',
    author: 'CCC Team',
    date: 'April 20, 2025',
    cover: 'https://images.unsplash.com/photo-1518830518637-a10f31eaea3e?auto=format&fit=crop&w=1200&q=80',
    tags: ['Mindfulness', 'Breathwork'],
    excerpt: 'Breathe deeply and allow yourself to release tension. Each breath is an invitation to come back to yourself.',
    body: [
      'The breath is one of the few tools that speaks directly to your nervous system in real time.',
      'Slow, extended exhales tell your body it’s safe to soften.',
      'A single mindful breath can shift a whole moment. And a whole moment can shift a whole day.',
    ],
  },
  {
    slug: 'what-is-emdr-therapy',
    title: 'What Is EMDR Therapy and How Can It Help?',
    author: 'CCC Team',
    date: 'March 15, 2025',
    cover: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1200&q=80',
    tags: ['EMDR', 'Trauma'],
    excerpt: 'EMDR is a widely researched, integrative psychotherapy approach. Here’s what it is — and how it can help.',
    body: [
      'Eye Movement Desensitization and Reprocessing (EMDR) is an evidence-based approach for helping the brain process distressing experiences.',
      'It has been extensively researched and is recognized as an effective treatment for trauma, anxiety, and related concerns.',
      'If you’re curious whether EMDR might help you, we’d love to talk with you about it.',
    ],
  },
  {
    slug: 'today-is-a-new-day-to-shine',
    title: 'Today Is a New Day to Shine',
    author: 'CCC Team',
    date: 'February 25, 2025',
    cover: 'https://images.unsplash.com/photo-1470137237906-d8a4f71e1966?auto=format&fit=crop&w=1200&q=80',
    tags: ['Encouragement'],
    excerpt: 'Every day is a gift. Today — whatever it holds — is another chance to let your light shine.',
    body: [
      'Some days feel heavy. Some feel light. Every one of them is another chance to try again.',
      'You don’t have to shine brightly. Even a soft, steady light is enough.',
      'Whoever you are and wherever you are today, you deserve to be here.',
    ],
  },
  {
    slug: 'laughing-out-loud',
    title: 'Laughing Out Loud',
    author: 'CCC Team',
    date: 'January 30, 2025',
    cover: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=1200&q=80',
    tags: ['Joy', 'Self-Care'],
    excerpt: 'Sometimes healing sounds like laughter. Never underestimate the medicine of a really good laugh.',
    body: [
      'Joy and healing are not opposites. Laughter can be a form of nervous system reset all its own.',
      'It doesn’t make hard things disappear, but it does remind us that we’re still here — still capable of delight.',
      'Find a small reason to laugh out loud today. Consider it therapy.',
    ],
  },
  {
    slug: 'imperfection-and-letting-go',
    title: 'Imperfection and Letting Go',
    author: 'CCC Team',
    date: 'January 5, 2025',
    cover: 'https://images.unsplash.com/photo-1499988921418-b7df40ff03f9?auto=format&fit=crop&w=1200&q=80',
    tags: ['Self-Compassion', 'Perfectionism'],
    excerpt: 'Waiting to be “perfect” before you share your voice? Consider this your gentle invitation to let go.',
    body: [
      'Perfectionism keeps us safe by keeping us small. It promises protection and delivers exhaustion.',
      'You will always be beautifully, humanly imperfect. That’s not a flaw — it’s the invitation.',
      'Let go a little. Share your voice a little sooner than feels comfortable. That’s where connection lives.',
    ],
  },
  {
    slug: 'the-journey-begins',
    title: 'The Journey Begins',
    author: 'Chesapeake Counseling Center',
    date: 'December 1, 2024',
    cover: 'https://images.unsplash.com/photo-1476611317561-60117649dd94?auto=format&fit=crop&w=1200&q=80',
    tags: ['Welcome'],
    excerpt: 'Thanks for joining us. We’re so glad to be here, sharing ideas and support with you.',
    body: [
      'This blog is a space for reflection, tools, and encouragement — written for the people we serve, and for anyone who needs a little support today.',
      'Some posts will be practical. Some will be softer. All of them are offered with care.',
      'Welcome. We’re glad you’re here.',
    ],
  },
];

export const WHY_CCC = [
  { icon: 'Clock', title: 'Rapid Appointments', text: 'We work to see new clients quickly, because waiting for help shouldn’t be part of the struggle.' },
  { icon: 'Users', title: 'Carefully Selected Clinicians', text: 'Each of our therapists is chosen for both clinical excellence and genuine warmth.' },
  { icon: 'Home', title: 'Warm, Comfortable Environment', text: 'A welcoming space designed to feel safe from the moment you walk in.' },
  { icon: 'BookOpen', title: 'Evidence-Based Treatment', text: 'Proven, research-informed approaches tailored to your unique story.' },
  { icon: 'Heart', title: 'Personalized Care', text: 'No two paths are the same. Your care is shaped around you.' },
  { icon: 'MapPin', title: 'In-Person Appointments', text: 'Meet us in our Chesapeake, VA office for face-to-face sessions.' },
  { icon: 'Video', title: 'Telehealth Options', text: 'Secure online sessions available throughout Virginia.' },
];
